import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import './PublicWeddingView.css';
import {
    Calendar,
    Image as ImageIcon,
    Users,
    Clock,
    MapPin,
    ShieldCheck,
    ChevronRight,
    Music,
    ArrowDown,
    Volume2,
    VolumeX,
    Search,
    UtensilsCrossed
} from 'lucide-react';

const PublicWeddingView: React.FC = () => {
    const { accessKey } = useParams<{ accessKey: string }>();
    const location = useLocation();
    const history = useHistory();

    const [wedding, setWedding] = useState<any>(null);
    const [ceremonies, setCeremonies] = useState<any[]>([]);
    const [images, setImages] = useState<any[]>([]);
    const [posts, setPosts] = useState<any[]>([]);
    const [lostFoundItems, setLostFoundItems] = useState<any[]>([]);
    const [menuCategories, setMenuCategories] = useState<any[]>([]);
    const [expandedMenu, setExpandedMenu] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        const fetchPublicData = async () => {
            const queryParams = new URLSearchParams(location.search);
            const t = queryParams.get('t');
            const s = queryParams.get('s');

            if (!accessKey || !t || !s) {
                setError('Invalid access link. Please ask the hosts for a new QR code.');
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`${API_BASE_URL}/api/public/wedding/${accessKey}?t=${t}&s=${s}`);
                const data = await res.json();

                if (res.ok) {
                    setWedding(data.wedding);
                    setCeremonies(data.ceremonies || []);
                    setImages(data.images || []);
                    setPosts(data.posts || []);
                    setLostFoundItems(data.lostFoundItems || []);
                    const cats = data.menuCategories || [];
                    setMenuCategories(cats);
                    if (cats.length > 0) setExpandedMenu({ [cats[0]._id]: true });
                } else {
                    setError(data.message || 'Access denied');
                }
            } catch (err) {
                setError('Connection error. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchPublicData();
    }, [accessKey, location.search]);

    if (loading) {
        return (
            <div className="public-loading-screen">
                <div className="loader-content">
                    <div className="heart-loader"></div>
                    <p>Unlocking Wedding Memories...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-error-screen">
                <div className="error-card glass-panel">
                    <ShieldCheck size={48} className="error-icon" />
                    <h2>Access Restricted</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="retry-btn">Retry Access</button>
                </div>
            </div>
        );
    }

    const access = wedding?.publicAccess;

    return (
        <div className="public-wedding-container">
            {/* Hero Section */}
            <header className="public-hero">
                <div className="hero-banner">
                    {wedding?.bannerImage ? (
                        <img src={getImageUrl(wedding.bannerImage)} alt="Wedding Banner" />
                    ) : (
                        <div className="banner-placeholder"></div>
                    )}
                    <div className="hero-overlay"></div>
                </div>

                <div className="hero-content">
                    <div className="wedding-badge">Official Digital Invitation</div>
                    <h1 className="couple-name">{wedding?.coupleName}</h1>
                    <div className="wedding-meta">
                        <div className="meta-item">
                            <Calendar size={18} />
                            <span>{new Date(wedding?.weddingDate).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                            })}</span>
                        </div>
                        <div className="meta-item">
                            <MapPin size={18} />
                            <span>{wedding?.city}</span>
                        </div>
                    </div>

                    {/* Guest Hub Navigation */}
                    <div className="guest-nav-grid">
                        {access?.schedule && (
                            <button className="nav-card" onClick={() => document.getElementById('schedule-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                <span className="nav-icon">📅</span>
                                <span className="nav-label">Schedule</span>
                            </button>
                        )}
                        {access?.gallery && (
                            <button className="nav-card" onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                <span className="nav-icon">🖼️</span>
                                <span className="nav-label">Gallery</span>
                            </button>
                        )}
                        {access?.guestWall && (
                            <button className="nav-card" onClick={() => document.getElementById('wall-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                <span className="nav-icon">📝</span>
                                <span className="nav-label">Guest Wall</span>
                            </button>
                        )}
                        {access?.lostFound && (
                            <button className="nav-card" onClick={() => document.getElementById('lostfound-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                <span className="nav-icon">🔍</span>
                                <span className="nav-label">Lost & Found</span>
                            </button>
                        )}
                        {access?.foodMenu && (
                            <button className="nav-card" onClick={() => document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                <span className="nav-icon">🍽️</span>
                                <span className="nav-label">Food Menu</span>
                            </button>
                        )}
                    </div>

                    <div className="scroll-hint" onClick={() => document.getElementById('schedule-section')?.scrollIntoView({ behavior: 'smooth' })}>
                        <span>Scroll to Explore</span>
                        <ArrowDown size={20} />
                    </div>
                </div>
            </header>

            {/* Content Modules */}
            <main className="public-main-content">
                {/* 1. Event Schedule */}
                {access?.schedule && (
                    <section className="access-section" id="schedule-section">
                        <div className="section-header">
                            <div className="title-block">
                                <Clock size={24} className="accent-icon" />
                                <h2>Celebration Schedule</h2>
                            </div>
                        </div>
                        {ceremonies.length > 0 ? (
                            <div className="ceremony-timeline">
                                {ceremonies.map((c, i) => (
                                    <div key={c._id} className="timeline-item">
                                        <div className="time-col">
                                            <span className="time-bubble">{c.time}</span>
                                        </div>
                                        <div className="info-card glass-panel">
                                            <h3>{c.name}</h3>
                                            <p className="venue"><MapPin size={14} /> {c.venue}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-module-glass">
                                <p>Schedule details are being finalized by the hosts. Check back soon!</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 2. Photo Gallery */}
                {access?.gallery && (
                    <section className="access-section" id="gallery-section">
                        <div className="section-header">
                            <div className="title-block">
                                <ImageIcon size={24} className="accent-icon" />
                                <h2>Wedding Gallery</h2>
                            </div>
                            {images.length > 0 && (
                                <button
                                    className="view-all-btn"
                                    onClick={() => history.push(`/w/${accessKey}/gallery${location.search}`)}
                                >
                                    View Full Album <ChevronRight size={16} />
                                </button>
                            )}
                        </div>
                        {images.length > 0 ? (
                            <div className="public-grid gallery-preview">
                                {images.slice(0, 6).map((img) => (
                                    <div key={img._id} className="grid-item">
                                        <img src={getImageUrl(img.imageUrl)} alt="Gallery Preview" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-module-glass photo-placeholder">
                                <p>Capturing beautiful moments... Photos will appear here soon!</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 3. Messages / Wall of Love */}
                {access?.guestWall && (
                    <section className="access-section" id="wall-section">
                        <div className="section-header">
                            <div className="title-block">
                                <Users size={24} className="accent-icon" />
                                <h2>Wall of Love</h2>
                            </div>
                        </div>
                        {posts.length > 0 ? (
                            <div className="messages-carousel">
                                {posts.map((post) => (
                                    <div key={post._id} className="message-card glass-panel">
                                        <p className="caption">"{post.caption}"</p>
                                        <span className="author">- Shared by a Guest</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-module-glass">
                                <p>Be the first to share a blessing on the Wall of Love!</p>
                            </div>
                        )}
                    </section>
                )}
                {/* 4. Lost & Found Board */}
                {access?.lostFound && (
                    <section className="access-section" id="lostfound-section">
                        <div className="section-header">
                            <div className="title-block">
                                <Search size={24} className="accent-icon" />
                                <h2>Lost & Found</h2>
                            </div>
                        </div>
                        {lostFoundItems.length > 0 ? (
                            <div className="public-grid" style={{ gap: '14px' }}>
                                {lostFoundItems.map((item: any) => (
                                    <div key={item._id} className="message-card glass-panel" style={{ padding: '16px', borderTop: item.type === 'lost' ? '3px solid #ef4444' : '3px solid #22c55e' }}>
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '3px 10px',
                                            borderRadius: '20px',
                                            fontSize: '0.7rem',
                                            fontWeight: 800,
                                            marginBottom: '8px',
                                            background: item.type === 'lost' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                            color: item.type === 'lost' ? '#dc2626' : '#16a34a'
                                        }}>
                                            {item.type === 'lost' ? '🔴 LOST' : '🟢 FOUND'}
                                        </span>
                                        {item.imageUrl && (
                                            <img src={getImageUrl(item.imageUrl)} alt={item.itemName} style={{ width: '100%', borderRadius: '10px', maxHeight: '140px', objectFit: 'cover', marginBottom: '8px' }} />
                                        )}
                                        <h4 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '1rem' }}>{item.itemName}</h4>
                                        <p className="caption" style={{ fontSize: '0.82rem', marginBottom: '8px' }}>{item.description}</p>
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '10px' }}>
                                            📍 {item.location} · 👤 {item.contactName}
                                        </div>
                                        {item.phoneNumber !== '***HIDDEN***' ? (
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <a href={`tel:${item.phoneNumber}`} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', borderRadius: '10px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', textDecoration: 'none', fontSize: '0.78rem', fontWeight: 700 }}>
                                                    📞 Call
                                                </a>
                                                <a
                                                    href={`https://wa.me/${item.phoneNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi ${item.contactName}, I'm at the wedding and saw your post about the "${item.itemName}"...`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', borderRadius: '10px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', textDecoration: 'none', fontSize: '0.78rem', fontWeight: 700 }}
                                                >
                                                    💬 WhatsApp
                                                </a>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: '0.75rem', color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '8px', textAlign: 'center' }}>
                                                🔒 Contact info is private
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-module-glass">
                                <p>No items reported yet. All good! 🎉</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 5. Food Menu */}
                {access?.foodMenu && menuCategories.length > 0 && (
                    <section className="access-section" id="menu-section">
                        <div className="section-header">
                            <div className="title-block">
                                <UtensilsCrossed size={24} className="accent-icon" />
                                <h2>Wedding Menu</h2>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {menuCategories.filter((c: any) => !c.isBeverage).map((cat: any) => (
                                <div key={cat._id} className="glass-panel" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <button
                                        onClick={() => setExpandedMenu(p => ({ ...p, [cat._id]: !p[cat._id] }))}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                                    >
                                        <span style={{ fontSize: '1.4rem' }}>{cat.icon}</span>
                                        <span style={{ flex: 1, fontWeight: 800, fontSize: '0.95rem', color: '#1c1917' }}>{cat.name}</span>
                                        <span style={{ fontSize: '0.7rem', color: '#a8a29e' }}>{cat.items.length} items</span>
                                        <span style={{ fontSize: '0.7rem', color: '#a8a29e' }}>{expandedMenu[cat._id] ? '▲' : '▼'}</span>
                                    </button>
                                    {expandedMenu[cat._id] && (
                                        <div style={{ padding: '0 16px 14px' }}>
                                            {cat.items.map((item: any, idx: number) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderBottom: idx < cat.items.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                                                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', border: `2px solid ${item.isVeg ? '#22c55e' : '#ef4444'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '3px' }}>
                                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.isVeg ? '#22c55e' : '#ef4444' }}></span>
                                                    </span>
                                                    <div>
                                                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1c1917', display: 'block' }}>{item.name}</span>
                                                        {item.description && <span style={{ fontSize: '0.78rem', color: '#78716c', display: 'block', marginTop: '2px' }}>{item.description}</span>}
                                                        {item.tags && item.tags.length > 0 && (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                {item.tags.map((t: string) => (
                                                                    <span key={t} style={{ padding: '1px 7px', borderRadius: '10px', fontSize: '0.6rem', fontWeight: 700, border: '1px solid #d1d5db', color: '#6b7280' }}>{t}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Beverages */}
                            {menuCategories.some((c: any) => c.isBeverage) && (
                                <>
                                    <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
                                        <span style={{ fontSize: '1.8rem' }}>🍹</span>
                                        <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#7c3aed', margin: '4px 0 0' }}>Beverages & Bar</h3>
                                    </div>
                                    {menuCategories.filter((c: any) => c.isBeverage).map((cat: any) => (
                                        <div key={cat._id} className="glass-panel" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(168,85,247,0.2)' }}>
                                            <button
                                                onClick={() => setExpandedMenu(p => ({ ...p, [cat._id]: !p[cat._id] }))}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                                            >
                                                <span style={{ fontSize: '1.4rem' }}>{cat.icon}</span>
                                                <span style={{ flex: 1, fontWeight: 800, fontSize: '0.95rem', color: '#1c1917' }}>{cat.name}</span>
                                                <span style={{ fontSize: '0.7rem', color: '#a8a29e' }}>{cat.items.length}</span>
                                                <span style={{ fontSize: '0.7rem', color: '#a8a29e' }}>{expandedMenu[cat._id] ? '▲' : '▼'}</span>
                                            </button>
                                            {expandedMenu[cat._id] && (
                                                <div style={{ padding: '0 16px 14px' }}>
                                                    {cat.items.map((item: any, idx: number) => (
                                                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderBottom: idx < cat.items.length - 1 ? '1px solid rgba(168,85,247,0.1)' : 'none' }}>
                                                            <span style={{ fontSize: '1.1rem', marginTop: '1px' }}>{cat.icon}</span>
                                                            <div>
                                                                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1c1917', display: 'block' }}>{item.name}</span>
                                                                {item.description && <span style={{ fontSize: '0.78rem', color: '#78716c', display: 'block', marginTop: '2px' }}>{item.description}</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </section>
                )}

                <div className="powered-by">
                    <p>Powered by <span>Shadi Gram</span></p>
                    <small>Digital Heritage for Modern Weddings</small>
                </div>
            </main>

            {/* Sticky Audio Player if theme song exists */}
            {wedding?.themeSongUrl && (
                <div className="public-audio-bar">
                    <div className="audio-info">
                        <Music size={20} className={!isMuted ? "pulse" : ""} />
                        <span>{!isMuted ? "Wedding Theme Playing..." : "Music Muted"}</span>
                    </div>
                    <button className="mute-toggle-btn" onClick={() => setIsMuted(!isMuted)}>
                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                    <audio src={getImageUrl(wedding.themeSongUrl)} autoPlay loop muted={isMuted} />
                </div>
            )}
        </div>
    );
};

export default PublicWeddingView;
